import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from 'react-router-dom';

import AppPage from '@/ui/AppPage/AppPage';
import Permissions from '@/lib/permissions';
import MainMap from '@/ui/Map/MainMap';

import mapService from '@/services/mapService';

import UserContext, { UserContextType } from '@/context/UserContext/UserContext';
import TenantContext, { TenantContextType } from '@/context/TenantContext/TenantContext';
import SnackbarContext from "@/context/SnackbarContext/SnackbarContext";

const MAX_VIEWS = 50;

const MapPage = () => {
  const userContext = useContext(UserContext) as UserContextType;
  const tenantContext = useContext(TenantContext) as TenantContextType;
  const { showNotification } = useContext(SnackbarContext)
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [mapId, setMapId] = useState<number | null>(null);
  const [shareLoaded, setShareLoaded] = useState(false);
  const [localStorageKey, setLocalStorageKey] = useState<string | undefined>();
  const hash = new URLSearchParams(window.location.search).get('hash');

  useEffect(() => {
    setShareLoaded(false);
    if (!userContext.loaded) return;
    const initialMapId = userContext.hasAnyPermission([Permissions.Private]) ? 1 : 2;
    setMapId(initialMapId);

    if (!hash) {
      setShareLoaded(true);
      return;
    }
    const fetchShare = async () => {
      const stateConfig = location.state?.config;
      let data;
      if (stateConfig) {
        data = JSON.parse(stateConfig);
      } else {
        const resp = await mapService.loadPrintConfig(hash);
        if (resp.success && resp.data) {
          // @ts-ignore
          data = JSON.parse(resp.data.config);
        } else {
          setShareLoaded(true);
          showNotification(t('map:sidebar.qrcode_error'), "error");
          return;
        }
      }
      localStorage.setItem('maplayers', data.maplayers);
      localStorage.setItem('mapbaselayers', data.mapbaselayers);

      const key = data.mapId === 1
        ? `viewsState_${userContext?.username}`
        : data.mapId === 2
          ? `viewsState_${tenantContext?.publicUsername}`
          : '';
      setLocalStorageKey(key);
      const existingRaw = localStorage.getItem(key);
      const existing: any = existingRaw ? JSON.parse(existingRaw) : { views: [], currentViewIndex: 0 };
      const newState = JSON.parse(data.viewsState);
      existing.views.push(...(newState.views || []));
      existing.currentViewIndex += 1;
      if (existing.views.length > MAX_VIEWS) {
        const trimmedViews = existing.views.slice(-MAX_VIEWS);
        const adjustedIndex = Math.min(existing.currentViewIndex, MAX_VIEWS);

        localStorage.setItem(key, JSON.stringify({
          views: trimmedViews,
          currentViewIndex: adjustedIndex,
          viewSwitchClicked: false,
          navigateTo: undefined,
        }));
      } else {
        localStorage.setItem(key, JSON.stringify(existing));
      }

      const newMapId = data.mapId;
      if (newMapId === 1 && !userContext.hasAnyPermission([Permissions.Private])) {
        navigate('/login', {
          state: { from: window.location.pathname + window.location.search },
        });
      } else {
        setMapId(newMapId);
      }
      setShareLoaded(true);
    };

    fetchShare();
  }, [userContext.loaded, hash]);

  return (
    <AppPage titlett="menu.map" needPermission={[Permissions.Public, Permissions.Private]}>
      {mapId !== null && shareLoaded && <MainMap map_id={mapId} local_storage_key={localStorageKey} />}
    </AppPage>
  );
};

export default MapPage;